import { useMutation, useQueryClient } from '@tanstack/react-query';

import { conceptGroupKeys } from '@verantos-dev/feature-concept-group-detail';
import { conceptGroupPreviewKeys } from '@verantos-dev/feature-epi-chat';

import { AuthFetchError, authFetch } from './authFetch';
import { projectPageKeys } from './queryKeys';

export interface RenameConceptGroupInput {
  conceptGroupId: string;
  conceptGroupName: string;
  /** Draft only — its preview id, so the Draft review surface picks up the new name. */
  previewId?: string | null;
}

/** Why a rename was refused, for picking the message the researcher sees. */
export type ConceptGroupRenameFailure = 'duplicate' | 'conflict' | 'error';

/**
 * Classify a rename rejection on the HTTP status rather than the message text:
 * 400 is the server's uniqueness/validation refusal, 409 a concurrent draft edit.
 */
export const classifyRenameFailure = (error: unknown): ConceptGroupRenameFailure => {
  if (!(error instanceof AuthFetchError)) return 'error';
  if (error.status === 400) return 'duplicate';
  if (error.status === 409) return 'conflict';
  return 'error';
};

/**
 * Rename a concept group from a project surface (OR-6657). PATCHes the stable id,
 * which the endpoint resolves against the published catalog and then the Draft
 * store, so the same call serves both the artifact list's dialog and the Draft
 * page's inline title edit.
 *
 * Duplicates the PATCH in `feature-concept-group-detail`'s `useRenameConceptGroup`
 * on purpose: that lib fetches through `core-utils`' `authFetch`, which discards the
 * HTTP status, and a rename has to tell a taken name apart from a stale draft.
 */
export const useRenameConceptGroupMutation = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, RenameConceptGroupInput>({
    mutationFn: ({ conceptGroupId, conceptGroupName }) =>
      authFetch<void>(`/concept-groups/${encodeURIComponent(conceptGroupId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ conceptGroupName }),
      }),
    onSuccess: (_data, { conceptGroupId, previewId }) => {
      // Name and Last modified on the list come from the project manifest, which no
      // concept-group key covers.
      queryClient.invalidateQueries({ queryKey: projectPageKeys.artifactsForProject(projectId) });
      queryClient.invalidateQueries({ queryKey: conceptGroupKeys.detail(conceptGroupId) });
      if (!previewId) return undefined;
      // Awaited, so the mutation settles only once the Draft page holds the new
      // name. Settling first closes the editor over a page still showing the old
      // name, and its name verdict with it.
      return queryClient.invalidateQueries({
        queryKey: conceptGroupPreviewKeys.prefix(previewId),
      });
    },
  });
};
